import { useEffect, useMemo, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { GenerateMagicLinkResponse, MagicLinkPurpose } from '@bower/shared';
import { safeAnalytics } from '../../utils/analytics';
import { ANALYTICS_EVENTS } from '../../constants/analytics';
import { apiService } from '../../services/api.service';
import { generateMagicLink } from '../../services/api/equipment.api';

const TTL_MS = 14 * 60 * 1000; // Refetch when < 1 min remains of 15-min TTL
const REFETCH_BEFORE_EXPIRY_MS = 60 * 1000;

/**
 * Purpose is part of the key, not a constant: two QRs can be on screen at once
 * (the onboarding checklist shows capture and live in the same section) and
 * they must not share a cached token — the purpose decides where the phone
 * lands, so a shared entry would send one of them to the wrong screen.
 */
const queryKeyFor = (purpose: MagicLinkPurpose) => ['magic-link', purpose] as const;

// The shared server contract, not a local restatement of it: a hand-rolled
// copy compiles happily while silently narrowing whatever the server adds.
async function fetchMagicLink(purpose: MagicLinkPurpose): Promise<GenerateMagicLinkResponse> {
  const res = await generateMagicLink(apiService, purpose);
  if (!res.success || !res.data) {
    throw new Error('Failed to generate magic link');
  }
  return res.data;
}

export function useMagicLink(purpose: MagicLinkPurpose = 'mobile_capture') {
  const queryClient = useQueryClient();
  const trackedQrRef = useRef(false);
  // Memoised: the expiry effect below depends on it, and a fresh array each
  // render would re-arm that timer on every render.
  const queryKey = useMemo(() => queryKeyFor(purpose), [purpose]);

  const query = useQuery({
    queryKey,
    queryFn: () => fetchMagicLink(purpose),
    staleTime: TTL_MS,
    gcTime: TTL_MS + REFETCH_BEFORE_EXPIRY_MS,
    retry: 2,
  });

  // Track generation and QR display once per mount
  useEffect(() => {
    if (query.data && !trackedQrRef.current) {
      trackedQrRef.current = true;
      safeAnalytics.track(ANALYTICS_EVENTS.MAGIC_LINK_GENERATED, { purpose });
      safeAnalytics.track(ANALYTICS_EVENTS.MAGIC_LINK_QR_DISPLAYED, { purpose });
    }
  }, [query.data, purpose]);

  // Schedule a refetch 1 minute before expiry
  useEffect(() => {
    if (!query.data) return;
    const expiresAt = new Date(query.data.expiresAt).getTime();
    const refetchAt = expiresAt - REFETCH_BEFORE_EXPIRY_MS;
    const delay = refetchAt - Date.now();
    if (delay <= 0) {
      queryClient.invalidateQueries({ queryKey });
      return;
    }
    const timer = setTimeout(() => {
      trackedQrRef.current = false;
      queryClient.invalidateQueries({ queryKey });
    }, delay);
    return () => clearTimeout(timer);
  }, [query.data, queryClient, queryKey]);

  return {
    qrUrl: query.data?.qrUrl ?? null,
    expiresAt: query.data?.expiresAt ?? null,
    isLoading: query.isLoading,
    isError: query.isError,
  };
}
